import { useFocusEffect } from 'expo-router';
import { useCallback, useState } from 'react';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import {
  ActionType,
  useAnalyticsScreenKey,
} from 'shared/hooks/analytics/useAnalyticsScreenKey';
import { PressableAnalyticsProps } from 'shared/hooks/analytics/usePressableAnalytics';
import { useValueEquivalentKeyValuesObject } from 'shared/hooks/useValueEquivalentKeyValuesObject/useValueEquivalentKeyValuesObject';
import { isNotNil } from 'shared/utils/isNotNil/isNotNil';
import { Maybe } from 'shared/utils/types';

/**
 * Log a Press analytics event specified in the config file.
 */
export const useStepStartedFocusAnalytics = (
  props: Maybe<PressableAnalyticsProps>,
) => {
  const { analyticsEvents } = useAnalyticsConfiguration();

  const { analyticsId, customAttributes } = props || {};

  const customAttributesValue = useValueEquivalentKeyValuesObject(
    customAttributes ?? null,
  );
  const hasCustomAttributes = isNotNil(customAttributes);
  const screenKey = useAnalyticsScreenKey(ActionType.PRESSABLE);
  const [registrationStepStarted, setRegistrationStepStarted] = useState(true);

  useFocusEffect(
    useCallback(() => {
      if (
        analyticsId &&
        ((hasCustomAttributes && customAttributesValue) || !hasCustomAttributes)
      ) {
        const key = screenKey + '/' + analyticsId;
        if (registrationStepStarted) {
          void analyticsEvents.logPressableEventFromConfig(
            key,
            customAttributes ?? null,
          );
          setRegistrationStepStarted(false);
        }
      }
    }, [
      analyticsId,
      hasCustomAttributes,
      customAttributesValue,
      screenKey,
      registrationStepStarted,
      analyticsEvents,
      customAttributes,
    ]),
  );
};

/**
 * Log a Press analytics event specified in the config file.
 */
export const useStatusChangedFocusAnalytics = (
  props: Maybe<PressableAnalyticsProps>,
) => {
  const { analyticsEvents } = useAnalyticsConfiguration();

  const { analyticsId, customAttributes } = props || {};

  const customAttributesValue = useValueEquivalentKeyValuesObject(
    customAttributes ?? null,
  );
  const hasCustomAttributes = isNotNil(customAttributes);
  const screenKey = useAnalyticsScreenKey(ActionType.PRESSABLE);
  const [statusChange, setStatusChange] = useState(true);
  useFocusEffect(
    useCallback(() => {
      if (
        analyticsId &&
        ((hasCustomAttributes && customAttributesValue) || !hasCustomAttributes)
      ) {
        const key = screenKey + '/' + analyticsId;
        if (statusChange) {
          void analyticsEvents.logPressableEventFromConfig(
            key,
            customAttributes ?? null,
          );
          setStatusChange(false);
        }
      }
    }, [
      analyticsId,
      hasCustomAttributes,
      customAttributesValue,
      screenKey,
      statusChange,
      analyticsEvents,
      customAttributes,
    ]),
  );
};
